import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ROUTES } from '@/components/gg/chrome'
import { SEED_COSTS, useSeedBalance, InsufficientSeedsError } from '@/hooks/useSeeds'
import {
  useFeedData, useFeedTuned, useBuildOk, useStartBuild, useDecide, useMorningPull, usePull, useFeedParams,
  useSendToPhone, useRecordProv, type RecordProv,
} from '@/hooks/useFeed'
import { useStoryVersions, useBuildVoice } from '@/hooks/useHeadlines'
import { VOICE_POSITIONS } from '@gogrov/shared'
import type { Voice } from '@/types/database'
import { useAddToNewsletter, useNewsletterItems, useRemoveFromNewsletter } from '@/hooks/useNewsletterItems'
import { FeedPhoto } from './FeedPhoto'
import { FeedSheet, useFeedToast } from './FeedUi'
import { STAGE_PCT, type FeedHeadline, type FeedStory } from './feedTypes'
import {
  ageOf, plainError, daysOld, isRecord, isStale, labelOf, noDash, plural, reportedAt, sourcesLabel,
} from './feedText'

/**
 * Your feed on a computer (Brian's desk.html v1.5): the stories as a list, the
 * chosen one open on the right. Search, filter chips (This week, Public
 * records, your top topics), a checkbox on each picture for bulk Build,
 * ＋ Newsletter or Not interested, and the keys J/K, B, X, N, S, / and ?.
 * A story built here is in the Library at once; the pane shows it writing.
 */

type Filter = 'all' | 'week' | 'records' | string

const words = (t: string) => (t.match(/\S+/g) ?? []).length
const paras = (t: string | null | undefined) => noDash(t).split(/\n{2,}/).filter((p) => p.trim()).map((p, i) => <p key={i}>{p}</p>)

/** What the photo matcher reads from a headline (pics.js lib(): id, topic, headline, dek). */
const picStory = (h: FeedHeadline) => ({ id: h.id, topic: h.topic, headline: h.headline, summary: h.summary })

export function DeskFeed() {
  const navigate = useNavigate()
  const { data, isFetched } = useFeedData()
  const tuned = useFeedTuned()
  const { data: balance } = useSeedBalance()
  const buildOk = useBuildOk()
  const startBuild = useStartBuild()
  const { pass } = useDecide()
  const pull = usePull()
  const nl = useNewsletterItems()
  const addNlM = useAddToNewsletter()
  const removeNlM = useRemoveFromNewsletter()
  const sendToPhone = useSendToPhone()
  const toast = useFeedToast()
  const [sel, setSel] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [fk, setFk] = useState<Filter>('all')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [showPassed, setShowPassed] = useState(false)
  const [nlBusy, setNlBusy] = useState<Set<string>>(new Set())
  const [keys, setKeys] = useState(false)
  const [sheet, setSheet] = useState<{ label: string; body: ReactNode } | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLElement>(null)
  const closeSheet = useCallback(() => setSheet(null), [])
  const available = balance?.total_available ?? 0

  // The day's five, as on the phone (no cards build in here: the list fills when they land).
  // /app?refresh=mix (Pull now on Topics): today's five again from the new mix, free.
  const { focus, clearFocus, refreshing } = useFeedParams(useCallback(() => {
    pull.mutate({ more: false, refresh: true })
    toast.show('Pulling today’s stories from your new mix. They appear as they land.', 2800)
  }, [pull, toast]))
  useMorningPull(data, isFetched, useCallback(() => pull.mutate({ more: false }), [pull]), refreshing || pull.isPending)

  const storyOf = useCallback((id: string): FeedStory | undefined => data?.stories.get(id), [data])
  const passed = useCallback((id: string) => data?.decided.get(id) === 'left' && !data?.stories.has(id), [data])
  // Built public records lead whatever their filing date (desk v1.3); then this week first.
  const all = useMemo(() => (data?.headlines ?? []).slice().sort((a, b) => Number(!isRecord(a)) - Number(!isRecord(b)) || reportedAt(b).localeCompare(reportedAt(a))), [data])
  const query = q.trim().toLowerCase()
  const matches = useCallback((h: FeedHeadline) =>
    (fk === 'all' || (fk === 'records' ? isRecord(h) : fk === 'week' ? daysOld(h) <= 7 : h.topic === fk))
    && (!query || `${h.headline} ${labelOf(h)} ${h.source_name ?? ''} ${h.summary ?? ''}`.toLowerCase().includes(query)), [fk, query])
  const visible = useMemo(() => all.filter((h) => (showPassed || !passed(h.id)) && matches(h)), [all, showPassed, passed, matches])
  const nPassed = all.filter((h) => passed(h.id)).length
  const tops = tuned.mix.slice(0, 4)
  const current = all.find((h) => h.id === sel) ?? null

  // /app?focus=<headlineId> (Emma's offers): open that story in the pane.
  useEffect(() => {
    if (!focus || !all.length) return
    if (all.some((h) => h.id === focus)) {
      setSel(focus); setFk('all'); setQ('')
      if (passed(focus)) setShowPassed(true)
      requestAnimationFrame(() => listRef.current?.querySelector(`[data-id="${CSS.escape(focus)}"]`)?.scrollIntoView({ block: 'center' }))
    }
    clearFocus()
  }, [focus, all]) // eslint-disable-line react-hooks/exhaustive-deps

  // The first story that's still to decide, once there are stories.
  useEffect(() => {
    if (sel || focus || !all.length) return
    const first = all.find((h) => !passed(h.id) && !storyOf(h.id)) ?? all[0]
    setSel(first.id)
  }, [all, sel, focus, passed, storyOf])

  // A story finished writing while another is open: "Ready: …" (desk v1).
  const prevWriting = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!data) return
    const now = new Set([...data.stories.values()].filter((x) => x.status === 'developing').map((x) => x.headline_id as string))
    for (const hid of prevWriting.current) {
      const st = data.stories.get(hid)
      if (st && st.status !== 'developing' && !now.has(hid) && hid !== sel) toast.show(`Ready: ${noDash(st.headline)}`)
    }
    prevWriting.current = now
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- actions ----------
  const doBuild = useCallback((list: FeedHeadline[], quiet = false) => {
    let n = 0, short = 0, left = available
    for (const h of list) {
      if (storyOf(h.id)) continue
      if (left < SEED_COSTS.build_story) { short++; continue }
      left -= SEED_COSTS.build_story; n++
      startBuild.mutate(h, {
        onError: (e) => {
          if (e instanceof InsufficientSeedsError) toast.show('This week’s stories are used. Everything else keeps working; they’re back Monday.', 3600)
          else toast.show(plainError(e, 'That build did not start. Nothing was charged.'), 3600)
        },
      })
    }
    if (!n && short) { toast.show('This week’s stories are used. Everything else keeps working; they’re back Monday.', 3600); return }
    if (!quiet || short) toast.show(`Building ${plural(n, 'story', 'stories')} · ${n * SEED_COSTS.build_story} seeds.${short ? ` ${short} more need seeds you don’t have this week.` : ''}`, 3400)
  }, [available, startBuild, storyOf, toast])

  const build = useCallback((list: FeedHeadline[]) => {
    const todo = list.filter((h) => !storyOf(h.id))
    if (!todo.length) return
    if (!buildOk.ok) {
      const cost = todo.length * SEED_COSTS.build_story
      setSheet({
        label: 'Build this story?',
        body: <>
          <h3>{todo.length === 1 ? 'Build this story?' : `Build ${todo.length} stories?`}</h3>
          <p className="feed-why">Emma writes the script and caption in your voice. <b>It uses {cost} of your {available.toLocaleString('en-US')} seeds.</b> Seeds come back every Monday; you’ll only be asked this once.</p>
          <button type="button" className="feed-sbtn feed-sbtn--pri" onClick={() => { closeSheet(); void buildOk.confirm(); doBuild(todo, todo.length === 1) }}>Build · {cost} seeds</button>
          <button type="button" className="feed-sbtn feed-sbtn--plain" onClick={closeSheet}>Not now</button>
        </>,
      })
      return
    }
    doBuild(todo, todo.length === 1)
  }, [available, buildOk, closeSheet, doBuild, storyOf])

  const move = useCallback((d: number) => {
    if (!visible.length) return
    const i = Math.max(0, visible.findIndex((h) => h.id === sel))
    const nx = visible[Math.min(visible.length - 1, Math.max(0, i + d))]
    setSel(nx.id)
    requestAnimationFrame(() => listRef.current?.querySelector(`[data-id="${nx.id}"]`)?.scrollIntoView({ block: 'nearest' }))
  }, [visible, sel])

  const notInterested = useCallback((list: FeedHeadline[], next = true) => {
    const ids = list.filter((h) => !passed(h.id) && !storyOf(h.id)).map((h) => h.id)
    if (!ids.length) return
    pass.mutate(ids)
    if (next && sel && ids.includes(sel)) {
      const nx = visible.find((h) => !ids.includes(h.id) && !passed(h.id) && !storyOf(h.id))
      setSel(nx ? nx.id : null)
    }
  }, [pass, passed, storyOf, sel, visible])

  const nlItem = useCallback((h: FeedHeadline) => {
    const s = storyOf(h.id)
    return nl.find({ headlineId: h.id, storyId: s && !s.id.startsWith('pending-') ? s.id : null })
  }, [nl, storyOf])

  const addNl = useCallback((h: FeedHeadline, quiet = false): boolean => {
    const s = storyOf(h.id)
    if (nlItem(h) || nlBusy.has(h.id)) return true
    if (available < SEED_COSTS.newsletter_version) return false
    setNlBusy((x) => new Set(x).add(h.id))
    const storyId = s && !s.id.startsWith('pending-') && s.status !== 'developing' ? s.id : null
    addNlM.mutate(storyId ? { storyId } : { headlineId: h.id }, {
      onSettled: () => setNlBusy((x) => { const n = new Set(x); n.delete(h.id); return n }),
      onSuccess: (r) => { if (!quiet) toast.show(<>In this week’s newsletter ({plural(r.count ?? nl.items.length + 1, 'story', 'stories')}). <Link to={ROUTES.newsletter}>Open it</Link></>, 3200) },
      onError: (e) => {
        if (e.status === 402 || /insufficient seeds/i.test(e.message)) toast.show('Not enough seeds left for that. This week’s come back Monday.', 3400)
        else toast.show(plainError(e, 'That didn’t go through. Nothing was charged.'), 3400)
      },
    })
    return true
  }, [available, addNlM, nl.items.length, nlBusy, nlItem, storyOf, toast])

  const removeNl = useCallback((h: FeedHeadline) => {
    const item = nlItem(h)
    if (item) removeNlM.mutate(item.id, { onSuccess: () => toast.show('Taken out of this week’s newsletter.') })
  }, [nlItem, removeNlM, toast])

  const inNl = (h: FeedHeadline) => !!nlItem(h)
  const togglePick = (id: string) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n })

  const bulk = (kind: 'build' | 'nl' | 'pass' | 'clear') => {
    const list = all.filter((h) => picked.has(h.id))
    if (kind === 'build') build(list)
    if (kind === 'pass') { notInterested(list); toast.show(`${list.length} marked not interested.`, 2400) }
    if (kind === 'nl') { let ok = 0; list.forEach((h) => { if (addNl(h, true)) ok++ }); toast.show(`${ok} going into this week’s newsletter.`, 2600) }
    setPicked(new Set())
  }

  // ---------- the keys ----------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target as HTMLElement | null
      if (t && t === searchRef.current) {
        if (e.key === 'Escape') { setQ(''); t.blur() } else if (e.key === 'Enter' || e.key === 'ArrowDown') { t.blur(); move(0) }
        return
      }
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return
      if (sheet) return
      const k = e.key.toLowerCase()
      if (k === 'j' || e.key === 'ArrowDown') { e.preventDefault(); move(1) }
      else if (k === 'k' || e.key === 'ArrowUp') { e.preventDefault(); move(-1) }
      else if (e.key === '/') { e.preventDefault(); searchRef.current?.focus() }
      else if (e.key === '?') setKeys(true)
      else if (e.key === 'Escape') { if (keys) setKeys(false); else if (picked.size) setPicked(new Set()) }
      else if (!current) return
      else if (k === 'b') build([current])
      else if (k === 'x' || k === 'p') { if (!passed(current.id) && !storyOf(current.id)) { notInterested([current], false); toast.show('Not interested.', 1600); move(1) } }
      else if (k === 'n') { if (!addNl(current)) toast.show('Not enough seeds left for that. This week’s come back Monday.', 3000) }
      else if (k === 's') togglePick(current.id)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  // ---------- drawing ----------
  const row = (h: FeedHeadline) => {
    const s = storyOf(h.id)
    const st = s ? (s.status === 'developing' ? <span className="dfeed-st">Writing…</span> : <span className="dfeed-st is-ready">Ready</span>)
      : passed(h.id) ? <span className="dfeed-st">Not interested</span> : null
    const rec = isRecord(h), old = isStale(h), on = picked.has(h.id)
    return (
      <button type="button" key={h.id} data-id={h.id} className="dfeed-row" aria-current={h.id === sel} onClick={() => setSel(h.id)}>
        <span className={`dfeed-ck${on ? ' is-on' : ''}`} role="checkbox" aria-checked={on} aria-label="Select" onClick={(e) => { e.stopPropagation(); togglePick(h.id) }}>{on ? '✓' : ''}</span>
        <span className="dfeed-thumb"><FeedPhoto url={h.image_url} topic={h.topic} seedKey={h.id} story={picStory(h)} /></span>
        <span className="dfeed-tp">{labelOf(h)}{inNl(h) && <span className="dfeed-tp__nl">· Newsletter</span>}{st}</span>
        <span className="dfeed-hl">{noDash(h.headline)}</span>
        <span className="dfeed-mt">{rec ? <span className="feed-seal feed-seal--sm">Public record</span> : <><span>{sourcesLabel(h)}</span>·</>}<span>{h.source_name}</span>·<span className={old ? 'feed-old' : ''}>{ageOf(h)}{old ? ' old' : ''}</span></span>
      </button>
    )
  }

  const head = (h: FeedHeadline) => {
    const rec = isRecord(h), old = isStale(h), d = daysOld(h)
    return <>
      <FeedPhoto className="dfeed-hero" url={h.image_url} credit={h.image_credit} topic={h.topic} seedKey={h.id} story={picStory(h)} />
      <span className="dfeed-chip">{labelOf(h)}</span>
      <h2 className="dfeed-h2">{noDash(h.headline)}</h2>
      <div className="dfeed-sline">
        {rec ? <span className="feed-seal feed-seal--sm">Public record</span> : <><span>{sourcesLabel(h)}</span>·</>}
        <span>{h.source_url ? <a href={h.source_url} target="_blank" rel="noopener noreferrer">{h.source_name}</a> : h.source_name}</span>·<span className={old ? 'feed-old' : ''}>{ageOf(h)}</span>
      </div>
      {old && <div className="dfeed-oldnote">From {d >= 14 ? `${Math.round(d / 7)} weeks` : `${d} days`} ago. Check it’s still current before you post.</div>}
    </>
  }

  const nlBtn = (h: FeedHeadline) => {
    const on = inNl(h), busy = nlBusy.has(h.id)
    return <button type="button" className={`dfeed-btn${on ? ' is-on' : ''}`} disabled={busy} onClick={() => (on ? removeNl(h) : addNl(h))}>
      {on ? '✓ In this week’s newsletter' : busy ? 'Writing the newsletter version…' : '＋ Newsletter'}
    </button>
  }

  const copy = (t: string, what: string) => { void navigator.clipboard?.writeText(t).catch(() => {}); toast.show(`${what} copied.`) }

  const pane = () => {
    const h = current
    if (!h) return <div className="dfeed-empty">{isFetched && !all.length ? (pull.isPending ? 'Your stories for today are on their way.' : 'You’re all caught up. Tomorrow’s stories land at 6am.') : 'Pick a story on the left.'}</div>
    const s = storyOf(h.id)
    if (s && s.status === 'developing') {
      const p = STAGE_PCT[s.build_stage ?? 'queued']
      return <div className="dfeed-in">{head(h)}
        <div className="dfeed-writing"><span className="dfeed-writing__bar"><i style={{ width: `${p}%` }} /></span><p>Writing the summary, the script and the caption… It’s in your Library already.</p></div>
      </div>
    }
    if (!s) {
      return <div className="dfeed-in">{head(h)}
        <div className="dfeed-acts">
          <button type="button" className="dfeed-btn dfeed-btn--go" onClick={() => build([h])}>Build · {SEED_COSTS.build_story}</button>
          <button type="button" className="dfeed-btn" onClick={() => notInterested([h])}>{passed(h.id) ? 'Not interested ✓' : 'Not interested'}</button>
          {nlBtn(h)}
        </div>
        {h.summary && <p className="dfeed-lead">{noDash(h.summary)}</p>}
        {h.angle && <div className="dfeed-angle"><div className="dfeed-k">Your angle</div><p>{noDash(h.angle)}</p></div>}
        {isRecord(h) && <ProvPanel headlineId={h.id} />}
      </div>
    }
    return <div className="dfeed-in">{head(h)}
      <DeskBuilt h={h} s={s} nlBtn={nlBtn(h)} copy={copy} topicVoice={tuned.mix.find((m) => m.topic === h.topic)?.voice as Voice | undefined}
        onRecord={() => navigate(`${ROUTES.record}?story=${encodeURIComponent(s.id)}`)}
        onPhone={() => sendToPhone.mutate(s.id, {
          onSuccess: () => toast.show('Sent to your phone. Open GoGrov there to record it with the teleprompter.', 3200),
          onError: () => toast.show('That didn’t reach your phone. Try again in a moment.', 3200),
        })}
        say={(m) => toast.show(m, 3400)} />
    </div>
  }

  const n = picked.size
  return (
    <div className="dfeed">
      <nav className="dfeed-list" ref={listRef} aria-label="Your stories">
        <div className="dfeed-tools">
          <input ref={searchRef} type="search" placeholder="Search stories" aria-label="Search stories" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="dfeed-chips">
            {([['all', 'All'], ['week', 'This week'], ['records', 'Public records'], ...tops.map((t) => [t.topic, labelOf({ topic: t.topic } as FeedHeadline)])] as [string, string][]).map(([k, l]) => (
              <button key={k} type="button" aria-pressed={fk === k} onClick={() => setFk(fk === k && k !== 'all' ? 'all' : k)}>{l}</button>
            ))}
          </div>
          {n ? (
            <div className="dfeed-bulk">
              <span className="dfeed-bulk__n">{n} selected</span>
              <button type="button" className="is-go" onClick={() => bulk('build')}>Build · {n * SEED_COSTS.build_story}</button>
              <button type="button" onClick={() => bulk('nl')}>＋ Newsletter</button>
              <button type="button" onClick={() => bulk('pass')}>Not interested</button>
              <button type="button" onClick={() => bulk('clear')}>Clear</button>
            </div>
          ) : (
            <div className="dfeed-keys"><kbd>J</kbd>/<kbd>K</kbd> move · <kbd>B</kbd> build · <kbd>X</kbd> not interested · <kbd>N</kbd> newsletter · <kbd>S</kbd> select · <kbd>/</kbd> search · <kbd>?</kbd> all</div>
          )}
        </div>
        <div className="dfeed-count"><span>{plural(visible.length, 'story', 'stories')}{query || fk !== 'all' ? ` of ${all.filter((h) => showPassed || !passed(h.id)).length}` : ''}</span><span>This week first</span></div>
        {visible.length ? visible.map(row) : <p className="dfeed-none">{all.length ? 'Nothing matches. Clear the search or pick All.' : pull.isPending ? 'Your stories for today are on their way.' : 'Nothing here yet.'}</p>}
        {nPassed > 0 && <button type="button" className="dfeed-passed" onClick={() => setShowPassed((v) => !v)}>{showPassed ? 'Hide' : 'Show'} the {nPassed} you passed</button>}
      </nav>
      <main className="dfeed-pane">{pane()}</main>

      {keys && (
        <div className="gg dfeed-kb" onClick={() => setKeys(false)}>
          <div className="dfeed-kb__in">
            <h3>Keyboard shortcuts</h3>
            {[['J or ↓', 'Next story'], ['K or ↑', 'Previous story'], ['B', 'Build'], ['X', 'Not interested'], ['N', 'Add to this week’s newsletter'], ['S', 'Select (for several at once)'], ['/', 'Search'], ['Esc', 'Clear the selection or the search']].map(([k, t]) => (
              <div key={k}><span>{t}</span><kbd>{k}</kbd></div>
            ))}
          </div>
        </div>
      )}
      <FeedSheet open={!!sheet} onClose={closeSheet} label={sheet?.label ?? ''}>{sheet?.body}</FeedSheet>
      {toast.node}
    </div>
  )
}

/** A public record's provenance: the record system, the document, the coverage check (desk provHTML). */
function ProvPanel({ headlineId, prov }: { headlineId: string; prov?: RecordProv | null }) {
  const { data } = useRecordProv(headlineId, prov === undefined)
  const p = prov ?? data
  if (!p) return null
  return (
    <div className="dfeed-prov">
      <div><div className="dfeed-k">Record system</div><div className="dfeed-v">{p.system}</div></div>
      <div><div className="dfeed-k">Document</div><div className="dfeed-v">{p.doc}</div></div>
      <div className="dfeed-prov__wide"><div className="dfeed-k">Coverage check</div><div className="dfeed-v">{p.check}</div></div>
    </div>
  )
}

const CLAIM: Record<string, string> = {
  newcomer: 'claims no authority, ends on a question',
  insider: 'speaks from your own standing, gives direction',
  reporter: 'claims the record’s authority, ends on what happens next',
}

/** Hashtags are the only pills (Brian, Oct 21). */
function CaptionText({ text }: { text: string }) {
  return <>{text.split(/\n{2,}/).filter((p) => p.trim()).map((para, i) => (
    <p key={i}>{para.split(/(#[\p{L}\p{N}_]+)/u).map((x, j) => x.startsWith('#') ? <span key={j} className="dfeed-hash">{x}</span> : x)}</p>
  ))}</>
}

/**
 * Built (desk v1.1): Record here, Send to your phone, ＋ Newsletter, Copy;
 * What's been reported beside the script in the three positions. Build wrote
 * the default; another is written when asked (1 seed, from the week); going
 * back to one is free.
 */
function DeskBuilt({ h, s, nlBtn, copy, topicVoice, onRecord, onPhone, say }: {
  h: FeedHeadline; s: FeedStory; nlBtn: ReactNode; copy: (t: string, what: string) => void; topicVoice?: Voice
  onRecord: () => void; onPhone: () => void; say: (m: string) => void
}) {
  const { data: versions } = useStoryVersions(s.id)
  const buildVoice = useBuildVoice()
  const { data: balance } = useSeedBalance()
  const def: Voice = topicVoice ?? 'newcomer'
  const cur = (s.voice ?? def) as Voice
  const [v, setV] = useState<Voice>(cur)
  const [writingV, setWritingV] = useState<Voice | null>(null)
  const textOf = (x: Voice) => (x === cur ? s.script : versions?.find((r) => r.voice === x)?.script) || null
  const have = new Set<Voice>([cur, ...(versions ?? []).map((r) => r.voice)])
  const txt = textOf(v)
  const written = have.has(v) && !!txt
  const caption = noDash(s.caption)
  const pos = VOICE_POSITIONS.find((p) => p.id === v)!
  const w = txt ? words(txt) : 0

  const write = () => {
    if ((balance?.total_available ?? 0) < SEED_COSTS.voice_build) { say('Not enough seeds left for that. This week’s come back Monday.'); return }
    setWritingV(v)
    buildVoice.mutate({ storyId: s.id, voice: v }, {
      onSettled: () => setWritingV(null),
      onError: (e) => say(plainError(e, 'That did not go through. You were not charged.')),
    })
  }

  return <>
    <div className="dfeed-acts">
      <button type="button" className="dfeed-btn dfeed-btn--go" onClick={onRecord}>Record here</button>
      <button type="button" className="dfeed-btn" onClick={onPhone}>Send to your phone</button>
      {nlBtn}
      <button type="button" className="dfeed-btn" onClick={() => copy(`${noDash(txt ?? s.script)}\n\n---\n\n${caption}`, 'Script and caption')}>Copy script and caption</button>
    </div>
    <div className="dfeed-grid">
      <section className="dfeed-card dfeed-sum"><div className="dfeed-hd"><span className="dfeed-k">{isRecord(h) ? 'What’s on the record' : 'What’s been reported'}</span><button type="button" className="dfeed-mini" onClick={() => copy(noDash(s.article), 'Summary')}>Copy</button></div>{paras(s.article)}{isRecord(h) && <ProvPanel headlineId={h.id} />}</section>
      <section className="dfeed-card dfeed-script">
        <div className="dfeed-hd">
          <div className="dfeed-tabs" role="group" aria-label="Position">
            {VOICE_POSITIONS.map((p) => (
              <button key={p.id} type="button" aria-pressed={p.id === v} onClick={() => setV(p.id as Voice)}>
                {p.label}{p.id === def && <small>default</small>}{!have.has(p.id as Voice) && <i className="dfeed-nw" title="Not written yet">not written</i>}
              </button>
            ))}
          </div>
          {written && <button type="button" className="dfeed-mini" onClick={() => copy(noDash(txt), 'Script')}>Copy</button>}
        </div>
        {written ? <>
          <div className="dfeed-txt">{paras(txt)}</div>
          <div className="dfeed-meta">{w} words · about {Math.round(w / 2.5)} seconds · {pos.closing}{v === def ? ` · your default for ${labelOf(h)}` : ''}</div>
        </> : writingV === v ? (
          <div className="dfeed-writing"><span className="dfeed-writing__bar"><i style={{ width: '70%' }} /></span><p>Writing it as {pos.label}…</p></div>
        ) : (
          <div className="dfeed-unwritten"><p>Not written as {pos.label} yet. {pos.label}: {CLAIM[v]}.</p><button type="button" className="dfeed-btn" onClick={write}>Write it as {pos.label}</button></div>
        )}
      </section>
    </div>
    {caption && <section className="dfeed-card dfeed-cap"><div className="dfeed-hd"><span className="dfeed-k">Caption</span><button type="button" className="dfeed-mini" onClick={() => copy(caption, 'Caption')}>Copy</button></div><CaptionText text={caption} /></section>}
  </>
}
